import { AlertTriangle, Save, FileText } from 'lucide-react';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';
import { DRAFT_PREFIX, getDraftItem, removeDraftItem } from '../utils/draftStorage';

/**
 * Modal dialog shown when user tries to close/navigate away from a dirty form.
 * Offers: Keep editing, Discard, Save as draft (optional), Save changes (optional).
 */
export function UnsavedChangesDialog({ open, onDiscard, onCancel, onSave, onSaveDraft }) {
  useBodyScrollLock(open);
  if (!open) return null;

  return (
    <div
      data-testid="unsaved-changes-dialog"
      className="fixed inset-0 z-[200] overflow-y-auto"
      style={{ background: 'var(--pd-overlay)', backdropFilter: 'blur(4px)' }}
    >
      <div
        className="flex min-h-full items-center justify-center px-4 py-8"
        onClick={onCancel}
      >
      <div
        className="w-full max-w-[400px] rounded-[22px] p-6 mx-4 animate-slide-up"
        style={{
          background: 'var(--pd-popover)',
          border: '1px solid var(--pd-popover-border)',
          boxShadow: '0 24px 48px rgba(0,0,0,0.35)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 mb-5">
          <div
            className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(180,83,9,0.08)' }}
          >
            <AlertTriangle className="w-5 h-5" style={{ color: 'var(--pd-warning)' }} />
          </div>
          <div>
            <h3
              className="text-[15px] font-semibold"
              style={{ color: 'var(--pd-text)', letterSpacing: '-0.02em' }}
            >
              Unsaved changes
            </h3>
            <p className="text-[12px] mt-0.5 leading-relaxed" style={{ color: 'var(--pd-muted)' }}>
              You have unsaved changes that will be lost if you leave.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 justify-end flex-wrap">
          <button
            data-testid="unsaved-keep-editing"
            onClick={onCancel}
            className="px-4 py-2.5 rounded-xl text-[12px] font-semibold transition-colors"
            style={{ color: 'var(--pd-muted)' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--pd-panel-2)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            Keep editing
          </button>
          <button
            data-testid="unsaved-discard"
            onClick={onDiscard}
            className="px-4 py-2.5 rounded-xl text-[12px] font-semibold transition-colors"
            style={{
              background: 'var(--pd-crimson-soft)',
              color: 'var(--pd-crimson)',
            }}
          >
            Discard
          </button>
          {onSaveDraft && (
            <button
              data-testid="unsaved-save-draft"
              onClick={onSaveDraft}
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-[12px] font-semibold transition-colors"
              style={{
                background: 'var(--pd-panel-2)',
                color: 'var(--pd-text)',
                border: '1px solid var(--pd-border)',
              }}
            >
              <FileText className="w-3.5 h-3.5" />
              Save draft
            </button>
          )}
          {onSave && (
            <button
              data-testid="unsaved-save"
              onClick={onSave}
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-[12px] font-semibold text-white transition-colors"
              style={{ background: 'var(--pd-focus)' }}
            >
              <Save className="w-3.5 h-3.5" />
              Save changes
            </button>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}

/**
 * Banner shown when a saved draft exists for a form.
 * Shows relative time ("just now", "3 days ago") and auto-hides for expired (>7d) drafts.
 */
export function DraftResumeBanner({ draftKey, onRestore, onDiscard }) {
  if (!draftKey) return null;

  let savedAt = '';
  try {
    const raw = getDraftItem(`${DRAFT_PREFIX}${draftKey}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.savedAt) {
      const date = new Date(parsed.savedAt);
      const now = new Date();
      const diffMs = now - date;
      const diffMin = Math.floor(diffMs / 60000);
      const diffH = Math.floor(diffMin / 60);
      const diffD = Math.floor(diffH / 24);
      if (diffMs > 7 * 24 * 60 * 60 * 1000) {
        // Expired — clean up silently
        removeDraftItem(`${DRAFT_PREFIX}${draftKey}`);
        return null;
      }
      if (diffMin < 1) savedAt = 'just now';
      else if (diffMin < 60) savedAt = `${diffMin}m ago`;
      else if (diffH < 24) savedAt = `${diffH}h ago`;
      else if (diffD === 1) savedAt = 'yesterday';
      else savedAt = `${diffD} days ago`;
    }
  } catch {
    return null;
  }

  return (
    <div
      data-testid="draft-resume-banner"
      className="flex items-center gap-3 px-4 py-3 rounded-2xl mb-3 animate-slide-up"
      style={{
        background: 'var(--pd-blue-soft)',
        border: '1px solid rgba(37,99,235,0.15)',
      }}
    >
      <FileText className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--pd-focus)' }} />
      <div className="flex-1 min-w-0">
        <span className="text-[12px] font-semibold" style={{ color: 'var(--pd-focus)' }}>
          Draft saved
        </span>
        {savedAt && (
          <span className="text-[11px] ml-1.5" style={{ color: 'var(--pd-muted)' }}>
            {savedAt}
          </span>
        )}
      </div>
      <button
        data-testid="draft-resume-btn"
        onClick={onRestore}
        className="px-3 py-1.5 rounded-lg text-[11px] font-semibold text-white"
        style={{ background: 'var(--pd-focus)' }}
      >
        Resume
      </button>
      <button
        data-testid="draft-discard-btn"
        onClick={onDiscard}
        className="px-3 py-1.5 rounded-lg text-[11px] font-semibold"
        style={{ color: 'var(--pd-muted)' }}
      >
        Discard
      </button>
    </div>
  );
}
